import React, { useCallback, useEffect, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { DropEvent, FileRejection, useDropzone } from 'react-dropzone';
import classNames from 'classnames';

interface GenericFilesUploadProps {
  onFilesDrop: (
    acceptedFiles: { id: string; files: File[] },
    fileRejections: FileRejection[],
    event: DropEvent,
  ) => void;
  errorMessageTranslationKey: string;
  id: string; // this an identifier of the dropzone
  disabled?: boolean;
  maxFiles?: number;
  maxSize?: number;
  acceptedFileTypes?: string[];
}

const GenericFilesUpload = ({
  onFilesDrop,
  errorMessageTranslationKey,
  id,
  disabled = false,
  maxFiles = 1,
  maxSize = 10000000, // 10MB
  acceptedFileTypes,
}: GenericFilesUploadProps) => {
  const [isSelectReject, setIsSelectReject] = useState(false);
  const [isSelectAccept, setIsSelectAccept] = useState(false);
  const [objectURLs, setObjectURLs] = useState<string[]>([]);
  const intl = useIntl();

  const handleOnDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[], event: DropEvent) => {
      if (acceptedFiles.length > 0) {
        onFilesDrop({ id, files: acceptedFiles }, fileRejections, event);
        const urls = acceptedFiles.map(file => URL.createObjectURL(file));
        setObjectURLs(urls);
        setIsSelectReject(false);
        setIsSelectAccept(true);
      } else {
        setIsSelectReject(true);
        setIsSelectAccept(false);
      }
    },
    [],
  );

  const { acceptedFiles, getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone({
      accept: acceptedFileTypes,
      maxSize,
      maxFiles,
      onDrop: handleOnDrop,
    });

  useEffect(() => {
    // Revoke the data uris to avoid memory leaks
    return () => objectURLs.forEach(url => URL.revokeObjectURL(url));
  }, [objectURLs]);

  let text = (
    <FormattedMessage
      id="settings.verify.uploadBox"
      tagName="p"
      values={{
        u: str => <u>{str}</u>,
      }}
    />
  );

  if (isSelectReject) {
    text = (
      <div className="flex flex-col">
        <h4>{intl.formatMessage({ id: 'settings.verify.upload.error.header' })}</h4>
        <FormattedMessage
          id={errorMessageTranslationKey}
          tagName="p"
          values={{
            u: str => <u>{str}</u>,
          }}
        />
      </div>
    );
  }

  if (acceptedFiles.length > 0 && !isSelectReject) {
    text = (
      <FormattedMessage
        id="settings.verify.proceed"
        tagName="p"
        values={{
          u: str => <u>{str}</u>,
        }}
      />
    );
  }

  return (
    <div id={id.toString()} className="flex flex-col gap-4 items-start">
      <div
        {...getRootProps()}
        className={classNames(
          `flex justify-center items-center text-center w-full border-2 rounded-md
            border-dashed cursor-pointer p-6 border-black/20 [&_h4]:m-0 [&_p]:font-bold
            [&_p]:text-sm [&_p]:leading-[1.5] [&_p]:m-0 [&_span]:underline`,
          { '!border-green-500': isDragAccept || isSelectAccept },
          { '!border-red-light': isDragReject || isSelectReject },
          { '!border-black': isDragActive },
          { '!cursor-not-allowed': disabled },
        )}
      >
        <input {...getInputProps()} disabled={disabled} />
        {text}
      </div>
      {acceptedFiles.length > 0 && (
        <div className="flex flex-col gap-4">
          {acceptedFiles.map((file, index) => (
            <div className="flex items-center gap-4" key={file.name}>
              {file.type === 'application/pdf' ? (
                <DocumentIcon className="h-4 w-4" />
              ) : (
                <img src={objectURLs[index]} className="h-10 w-auto" alt={file.name} />
              )}
              <span className="text-xs md:text-sm">{file.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GenericFilesUpload;
